import { useRefetchWhenPaneSaves } from "../../../main/panes/usePanes";
import { LegalEntityAuditGroupAuditsTabProps } from "./LegalEntityAuditGroupAuditsTab.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { DotLegalSelectOption } from "../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { LegalEntityAuditInformationsDto } from "../../../legalEntity/legalEntities/LegalEntities.types.ts";
import { htmlToPlainText, isNullOrWhitespace, setFirstLetterToLowerCase } from "../../../common/stringOperations.ts";
import { BaseViewModel } from "../../../system/systemOverview/SystemOverview.types.ts";
import { LegalEntityAuditStatus } from "../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types.ts";
import { RiskLevel } from "../../../legalEntity/legalEntityClassificationTab/LegalEntityClassificationTab.types.ts";
import {
    LegalEntityAuditInformation,
    LegalEntityClassificationInformationViewModel,
} from "../../legalEntityAuditGroupDraftOverview/auditGroupLegalEntitySelector/AuditGroupLegalEntitySelector.types.ts";
import { EvaluationStatus } from "../../../common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types.ts";
import { LegalEntityAuditGroupAuditModel, LegalEntityAuditGroupAuditsTabRow } from "../LegalEntityAuditGroupOverview.types.ts";
import { useDownloadLegalEntityAuditReport } from "../../DowloadLegalEntityAuditReport.hooks.ts";
import { useLegalEntityTypeNames } from "../../../legalEntity/useLegalEntityTypeNames.ts";
import { useDotLegalPostGetQuery } from "@common/hooks/dotLegalReactQuery";
import { EntityType } from "@common/Common.types.ts";
import { createElement, useState } from "react";
import { getDownloadDocumentVersionUrl } from "@common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks.ts";
import { Trans } from "react-i18next";
import { downloadFile } from "@common/api/downloadFile.ts";
import { ITableHeader, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useRowNoteDialog } from "@common/components/dotlegalNote/useRowNoteDialog";
import { LegalEntityContactType } from "../../../legalEntity/legalEntityContactTab/LegalEntityContactTab.types.ts";
import { getQuestionnaireResponseScore } from "../../../common/questionnaireResponseScore.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";
import { useFilterState } from "@common/hooks/useFilterState";
import { getUniqueFilterOptions } from "@common/hooks/filterUtils";
import {
    allLegalEntityAuditGroupAuditColumns,
    filterIds,
    LegalEntityAuditGroupAuditColumn,
    sortPropertyByColumn,
} from "./LegalEntityAuditGroupAuditsTab.columns.ts";

export function useLegalEntityAuditGroupAuditsTab(props: LegalEntityAuditGroupAuditsTabProps) {
    const { translateString, translateLegalEntityAuditStatus, translateEvaluationEnum, translateDate } = useTranslation();
    const { getLegalEntityTypeName } = useLegalEntityTypeNames();
    const { getReportPdfUrl, isDownloadingPdf, onDownloadReportAsPdf } = useDownloadLegalEntityAuditReport();
    const snackbar = useDotLegalSnackbar();
    useRefetchWhenPaneSaves(props.refetchData);

    const filterStateKey = `audit-group-audits-${props.auditGroupId}`;
    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(filterStateKey);
    const [searchString, setSearchString] = useStateSessionStorage<string>(`${filterStateKey}-search`, "");
    const [downloadingDocument, setDownloadingDocument] = useState("");

    const auditInformationsUrl = `/legalEntityAudit/auditInformations`;
    let auditInformationsQuery = useDotLegalPostGetQuery<Array<LegalEntityAuditInformation>>(
        auditInformationsUrl,
        {
            legalEntityIds: props.auditsData?.audits.map((x) => x.legalEntityId),
            fetchLatestAudit: false,
        } as LegalEntityAuditInformationsDto,
        {
            enabled: props.auditsData !== undefined,
        }
    );

    if (auditInformationsQuery.data) {
        auditInformationsQuery.data.forEach((s) => {
            let entity = props.auditsData?.audits.find((x) => x.legalEntityId === s.legalEntityId);

            if (entity) {
                entity.classifications = s.classifications;
            }
        });
    }

    const filters = {
        legalEntityOptions: Array<DotLegalSelectOption>(),
        typeSelectOptions: Array<DotLegalSelectOption>(),
        riskLevelOptions: Array<DotLegalSelectOption>(),
        assetsOptions: Array<DotLegalSelectOption>(),
        statusOptions: Array<DotLegalSelectOption>(),
        classificationOptions: Array<DotLegalSelectOption>(),
        evaluationOptions: Array<DotLegalSelectOption>(),
    };

    let rows: Array<LegalEntityAuditGroupAuditsTabRow> = [];
    if (props.auditsData && props.auditsData.audits.length > 0) {
        filters.legalEntityOptions = getUniqueFilterOptions(
            props.auditsData.audits,
            (audit) => audit.legalEntityName,
            (audit) => audit.legalEntityId
        );
        createTypeOptions(props.auditsData.audits.flatMap((x) => x.legalEntityTypes.flatMap((x) => x)));
        createAssetOptions(props.auditsData.audits.flatMap((x) => x.assets.flatMap((x) => x)));
        createStatusOptinos(props.auditsData.audits.flatMap((x) => x.status));
        createEvalutionOptions(props.auditsData.audits.filter((x) => x.evaluationStatus).map((x) => x.evaluationStatus!));
        createAuditRiskLevelOptions(props.auditsData.audits.filter((x) => x.status === LegalEntityAuditStatus.Submitted).flatMap((x) => x.riskLevel));

        createClassificationAndRiskLevelFilterOptions(auditInformationsQuery.data ? props.auditsData.audits.flatMap((x) => x.classifications) : []);

        sortSelectOptions();
        rows = toTableRows(getFilteredRows(props.auditsData.audits));
    }

    const columnLabels: Record<LegalEntityAuditGroupAuditColumn, string> = {
        [LegalEntityAuditGroupAuditColumn.LegalEntity]: translateString("legalEntity"),
        [LegalEntityAuditGroupAuditColumn.UniqueIdentifier]: translateString("id"),
        [LegalEntityAuditGroupAuditColumn.Type]: translateString("type"),
        [LegalEntityAuditGroupAuditColumn.Contact]: translateString("contact"),
        [LegalEntityAuditGroupAuditColumn.AdditionalRecipients]: translateString("additionalRecipients"),
        [LegalEntityAuditGroupAuditColumn.Assets]: translateString("assets"),
        [LegalEntityAuditGroupAuditColumn.Classifications]: translateString("classifications"),
        [LegalEntityAuditGroupAuditColumn.Status]: translateString("status"),
        [LegalEntityAuditGroupAuditColumn.RiskLevel]: translateString("riskLevel"),
        [LegalEntityAuditGroupAuditColumn.Score]: translateString("score"),
        [LegalEntityAuditGroupAuditColumn.Evaluation]: translateString("evaluation"),
        [LegalEntityAuditGroupAuditColumn.AuditReport]: translateString("legalEntityAuditReport"),
        [LegalEntityAuditGroupAuditColumn.ReminderSent]: translateString("headerReminderSent"),
        [LegalEntityAuditGroupAuditColumn.Note]: translateString("note"),
    };

    const tableHeaders: Array<ITableHeader<LegalEntityAuditGroupAuditsTabRow>> = allLegalEntityAuditGroupAuditColumns.map((column) => ({
        property: sortPropertyByColumn[column],
        text: columnLabels[column],
        align: "left",
        showOnMobile: column !== LegalEntityAuditGroupAuditColumn.Status && column !== LegalEntityAuditGroupAuditColumn.RiskLevel,
    }));

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: filterIds.legalEntity,
                    label: translateString("legalEntity"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters[filterIds.legalEntity] ?? [],
                    fixedData: filters.legalEntityOptions,
                },
                {
                    id: filterIds.type,
                    label: translateString("type"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters[filterIds.type] ?? [],
                    fixedData: filters.typeSelectOptions,
                    singleSelect: true,
                },
                {
                    id: filterIds.assets,
                    label: translateString("assets"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters[filterIds.assets] ?? [],
                    fixedData: filters.assetsOptions,
                },
                {
                    id: filterIds.classifications,
                    label: translateString("classifications"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters[filterIds.classifications] ?? [],
                    fixedData: filters.classificationOptions,
                },
                {
                    id: filterIds.status,
                    label: translateString("status"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters[filterIds.status] ?? [],
                    fixedData: filters.statusOptions,
                },
                {
                    id: filterIds.riskLevel,
                    label: translateString("riskLevel"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters[filterIds.riskLevel] ?? [],
                    fixedData: filters.riskLevelOptions,
                },
                {
                    id: filterIds.evaluations,
                    label: translateString("evaluation"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters[filterIds.evaluations] ?? [],
                    fixedData: filters.evaluationOptions,
                },
            ],
            filterOperators
        );
    }

    const onDownloadClick = async (documentId: string, dataId: string, fileName: string, link: string | undefined, entityId: string) => {
        if (!isNullOrWhitespace(fileName)) {
            setDownloadingDocument(documentId);
            await downloadDocumentVersion(documentId, dataId, fileName, EntityType.LegalEntity, entityId);
            setDownloadingDocument("");
        } else {
            window.open(link, "_blank");
        }
    };

    async function downloadDocumentVersion(documentId: string, dataId: string, fileName: string, entityType: EntityType, entityId: string) {
        let url = getDownloadDocumentVersionUrl(documentId, dataId, entityType, entityId);

        snackbar.show(createElement(Trans, { i18nKey: "documentIsBeingDownloaded", values: { document: fileName } }));
        await downloadFile(url, fileName);
    }

    const { getNoteMenuItem, noteDialogProps } = useRowNoteDialog<LegalEntityAuditGroupAuditModel>({
        getPostUrl: (row) => `/legalEntityAudit/${row.id}/note`,
        entityType: EntityType.LegalEntityAudit,
        onSaved: props.refetchData,
    });

    return {
        rows,
        tableHeaders,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        searchString,
        setSearchString,
        getReportPdfUrl,
        isDownloadingPdf,
        onDownloadReportAsPdf,
        auditInformations: auditInformationsQuery.data,
        auditInformationsIsLoading: auditInformationsQuery.isLoading,
        onDownloadClick,
        downloadingDocument,
        getNoteMenuItem,
        noteDialogProps,
    };

    function toTableRows(audits: Array<LegalEntityAuditGroupAuditModel>): Array<LegalEntityAuditGroupAuditsTabRow> {
        return audits.map((x) => ({
            ...x,
            assetsString: x.assets.flatMap((x) => x.name).join(", "),
            additionalRecipientNames: x.additionalRecipients.map((additionalRecipient) => additionalRecipient.name).join(", "),
            contactTypesString: x.contactTypes.map((x) => translateString(setFirstLetterToLowerCase(LegalEntityContactType[x].toString()))).join(", "),
            auditStatusString: translateLegalEntityAuditStatus(x.status.toString()),
            scoreString: getQuestionnaireResponseScore(
                x.status === LegalEntityAuditStatus.Submitted,
                x.scoreEnabled,
                x.score,
                translateString("scoreNotEnabled")
            ),
            legalEntityTypeString: x.legalEntityTypes?.map((type) => getLegalEntityTypeName(type)).join(", "),
            classificationsString: (x.classifications ?? []).map((classification) => classification.name).join(", "),
            reminderSentString: x.reminderSent ? translateDate(x.reminderSent) : "",
            notePlainText: htmlToPlainText(x.note),
        }));
    }

    function createTypeOptions(types: Array<string>) {
        for (let type of types) {
            if (!filters.typeSelectOptions.some((x) => x.id === type)) {
                filters.typeSelectOptions.push({
                    id: type,
                    name: getLegalEntityTypeName(type),
                });
            }
        }
    }

    function createAssetOptions(assets: Array<BaseViewModel>) {
        assets.forEach((asset) => {
            if (!filters.assetsOptions.some((x) => x.id == asset.id)) {
                filters.assetsOptions.push({ id: asset.id, name: asset.name });
            }
        });
    }

    function createStatusOptinos(allAuditStatus: Array<LegalEntityAuditStatus>) {
        allAuditStatus.forEach((status) => {
            if (!filters.statusOptions.some((x) => x.id == status.toString())) {
                filters.statusOptions.push({
                    id: status.toString(),
                    name: translateLegalEntityAuditStatus(status.toString()),
                });
            }
        });
    }

    function createAuditRiskLevelOptions(riskLevels: Array<RiskLevel | undefined>) {
        riskLevels.forEach((level) => {
            if (level && !filters.riskLevelOptions.some((x) => x.id == level.toString())) {
                filters.riskLevelOptions.push({
                    id: level.toString(),
                    name: translateString(setFirstLetterToLowerCase(RiskLevel[level])),
                });
            }
        });
    }

    function createClassificationAndRiskLevelFilterOptions(classifications: Array<LegalEntityClassificationInformationViewModel>) {
        classifications.forEach((classification) => {
            if (classification && !filters.classificationOptions.some((x) => x.id == classification.questionnaireId)) {
                filters.classificationOptions.push({ id: classification.questionnaireId, name: classification.name });
            }
        });
    }

    function createEvalutionOptions(allEvaluationStatus: Array<EvaluationStatus>) {
        allEvaluationStatus.forEach((status) => {
            if (!filters.evaluationOptions.some((x) => x.id == status.toString())) {
                filters.evaluationOptions.push({
                    id: status.toString(),
                    name: translateEvaluationEnum(status.toString()),
                });
            }
        });
    }

    function sortSelectOptions() {
        filters.legalEntityOptions.sort((a, b) => a.name.localeCompare(b.name));
        filters.typeSelectOptions.sort((a, b) => a.name.localeCompare(b.name));
        filters.classificationOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
        filters.riskLevelOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    // The classifications only arrive with the second request, so until then that filter matches nothing - the same as
    // when the filters lived in the URL.
    function getFilteredRows(audits: Array<LegalEntityAuditGroupAuditModel>) {
        return applyClientFilters(
            audits,
            [
                { id: filterIds.legalEntity, candidate: (row: LegalEntityAuditGroupAuditModel) => row.legalEntityId },
                { id: filterIds.type, candidate: (row: LegalEntityAuditGroupAuditModel) => row.legalEntityTypes.map((type) => type.toString()) },
                {
                    id: filterIds.classifications,
                    candidate: (row: LegalEntityAuditGroupAuditModel) =>
                        (row.classifications ?? [])
                            .filter((classification) => classification?.questionnaireId)
                            .map((classification) => classification.questionnaireId),
                },
                // A risk level of none is never offered, matching the old filter, which skipped falsy levels.
                { id: filterIds.riskLevel, candidate: (row: LegalEntityAuditGroupAuditModel) => (row.riskLevel ? row.riskLevel.toString() : undefined) },
                { id: filterIds.assets, candidate: (row: LegalEntityAuditGroupAuditModel) => row.assets.map((asset) => asset.id) },
                { id: filterIds.status, candidate: (row: LegalEntityAuditGroupAuditModel) => row.status.toString() },
                { id: filterIds.evaluations, candidate: (row: LegalEntityAuditGroupAuditModel) => row.evaluationStatus?.toString() },
            ],
            selectFilters,
            {},
            filterOperators
        )!;
    }
}
